import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';
import { type DisplayName, Region } from './constants';
import type { BaseAppContext } from '.';
import { Locale } from '@utils/models';
import { getDefaultLocaleByRegion } from './state';
import i18n from './i18n';
import { US_HOSTNAME_SUFFIX } from './hostnames';

const validateState = (state: BaseAppContext | null, operation: string): BaseAppContext => {
  if (state === null) {
    throw new Error(
      `AppContext was undefined when ${operation}. AppContext should be defined as soon as the application is starting.`
    );
  }
  return state;
};

const setLocaleAndChangeLanguage = (state: BaseAppContext, locale: Locale): void => {
  i18n.changeLanguage(locale);
  state.locale = locale;
};

export const detectRegionFromContext = (): Region => {
  if (typeof window === 'undefined') return Region.EU;

  const hostname = window.location.hostname;

  return hostname.endsWith(US_HOSTNAME_SUFFIX) ? Region.US : Region.EU;
};

const initialState: BaseAppContext = {
  locale: Locale.enGB,
  region: detectRegionFromContext(),
  displayName: '',
};

export const { reducer, actions: AppContextActions } = createSlice({
  name: 'APP_CONTEXT',
  initialState,
  reducers: {
    initialize: (_, action: PayloadAction<BaseAppContext>) => action.payload,

    initializeWithRegion: (
      _,
      action: PayloadAction<{
        appContext: Omit<BaseAppContext, 'locale'>;
        region: Region;
        overrideLocale?: Locale;
      }>
    ) => {
      const { appContext, region, overrideLocale } = action.payload;
      const locale = getDefaultLocaleByRegion(region, overrideLocale);

      i18n.changeLanguage(locale);

      return { ...appContext, region, locale };
    },

    initializeWithRegionDetection: (
      _,
      action: PayloadAction<{
        appContext: Omit<BaseAppContext, 'locale' | 'region'>;
        overrideLocale?: Locale;
      }>
    ) => {
      const { appContext, overrideLocale } = action.payload;
      const detectedRegion = detectRegionFromContext();
      const locale = getDefaultLocaleByRegion(detectedRegion, overrideLocale);

      i18n.changeLanguage(locale);

      return { ...appContext, region: detectedRegion, locale };
    },

    setLocale: (state, action: PayloadAction<Locale>) => {
      const validState = validateState(state, 'setting new locale');
      setLocaleAndChangeLanguage(validState, action.payload);
    },

    setLocaleWithFallback: (state, action: PayloadAction<{ overrideLocale?: Locale }>) => {
      const validState = validateState(state, 'setting new locale');
      const locale = getDefaultLocaleByRegion(validState.region, action.payload.overrideLocale);
      setLocaleAndChangeLanguage(validState, locale);
    },

    loadLocaleContent: (state, action: PayloadAction<{ locale: Locale; loadContent?: boolean }>) => {
      const validState = validateState(state, 'loading locale content');
      const { locale, loadContent = true } = action.payload;

      if (loadContent) {
        i18n.changeLanguage(locale);
      }

      validState.locale = locale;
    },

    setDisplayName: (state, action: PayloadAction<DisplayName>) => {
      validateState(state, 'setting display name').displayName = action.payload;
    },

    setRegion: (state, action: PayloadAction<{ region: Region; overrideLocale?: Locale; autoSetLocale?: boolean }>) => {
      const validState = validateState(state, 'setting region');
      const { region, overrideLocale, autoSetLocale = true } = action.payload;

      validState.region = region;

      if (autoSetLocale) {
        const locale = getDefaultLocaleByRegion(region, overrideLocale);
        setLocaleAndChangeLanguage(validState, locale);
      }
    },

    setRegionOnly: (state, action: PayloadAction<Region>) => {
      validateState(state, 'setting region').region = action.payload;
    },
  },
});

export const ActionPortalUtils = {
  initializeActionPortal: (appContext: Omit<BaseAppContext, 'locale' | 'region'>, overrideLocale?: Locale) =>
    AppContextActions.initializeWithRegionDetection({ appContext, overrideLocale }),

  loadLocaleContent: (locale: Locale) => AppContextActions.loadLocaleContent({ locale, loadContent: true }),

  getDefaultLocale: (region: Region, override?: Locale) => getDefaultLocaleByRegion(region, override),
};

interface State {
  appContext: BaseAppContext | null;
}

export const selectorBaseAppContext = (state: State): BaseAppContext => {
  const appContext = state.appContext;
  if (appContext === null || isEmpty(appContext)) {
    throw new Error('BaseAppContext is undefined, it should be defined as soon as an application has started.');
  }
  return appContext;
};

export const selectorDisplayName = (state: State) => selectorBaseAppContext(state).displayName;
export const selectorLocale = (state: State) => selectorBaseAppContext(state).locale;
export const selectorRegion = (state: State): Region | undefined => selectorBaseAppContext(state).region;

export const selectorDefaultLocaleForRegion = (state: State, overrideLocale?: Locale): Locale => {
  const region = selectorRegion(state);
  if (!region) {
    throw new Error('Region is undefined, cannot determine default locale.');
  }
  return getDefaultLocaleByRegion(region, overrideLocale);
};

export const selectorIsEuRegion = (state: State): boolean => selectorRegion(state) === Region.EU;

export const selectorIsUsRegion = (state: State): boolean => selectorRegion(state) === Region.US;

export const selectorRegionBasedLocale = (state: State): Locale => {
  const region = selectorRegion(state);
  return region ? getDefaultLocaleByRegion(region) : Locale.enUS;
};
